import request from '@/utils/request/index';

// ==================== Common ====================

export interface PageResult<T> {
  list: T[];
  totalCount: number;
  currentPage: number;
  totalPage: number;
}

interface PageQuery {
  size?: number;
  page?: number;
  isLimit?: boolean;
  lastId?: number;
}

// ==================== Dept & User ====================

export interface DeptTreeNode {
  id: number;
  parentId: number;
  name: string;
  level: number;
  children?: DeptTreeNode[];
}

export interface AdminUserItem {
  userId: string;
  realName: string;
  mobile: string;
  deptId: number;
  deptName: string;
  position: string;
  headPic: string;
  createTime: number;
}

export const feimaChatSuperAdminListUsers = (
  data: PageQuery & { keyword?: string },
  options?: any
) => request<PageResult<AdminUserItem>>('/admin/user/listUsers', 'POST', data, options);

export const feimaChatSuperAdminAddUser = (
  data: { realName: string; mobile: string; deptId: number; position: string; headPic?: string },
  options?: any
) => request<void>('/admin/user/addUser', 'POST', data, options);

export const feimaChatSuperAdminUpdateUser = (
  data: { id: string; realName: string; mobile: string; deptId: number; position: string; headPic?: string },
  options?: any
) => request<void>('/admin/user/updateUser', 'POST', data, options);

export const feimaChatSuperAdminDeleteUser = (
  data: { id: string },
  options?: any
) => request<void>('/admin/user/deleteUser', 'POST', data, options);

export const feimaChatSuperAdminGetDeptTree = (options?: any) =>
  request<DeptTreeNode[]>('/admin/dept/getDeptTree', 'POST', { parentId: '', level: 'ALL' }, options);

export const feimaChatSuperAdminAddDept = (
  data: { parentId?: number; name: string; leaderId?: string },
  options?: any
) => request<void>('/admin/user/addDept', 'POST', data, options);

export const feimaChatSuperAdminUpdateDept = (
  data: { id: number; name: string; leaderId?: string },
  options?: any
) => request<void>('/admin/user/updateDept', 'POST', data, options);

export const feimaChatSuperAdminDeleteDept = (
  data: { id: number },
  options?: any
) => request<void>('/admin/user/deleteDept', 'POST', data, options);

// ==================== Agent Creation List ====================

export interface AdminAgentItem {
  id: string;
  name: string;
  avatar: string;
  description: string;
  creatorName: string;
  createTime: string;
  usageCount: number;
  followCount: number;
  labels: string[];
}

export const feimaChatSuperAdminListAgents = (
  data: PageQuery & { keyword?: string; sortBy?: string },
  options?: any
) => request<PageResult<AdminAgentItem>>('/admin/agent/listAgents', 'POST', data, options);

export const feimaChatSuperAdminSaveAgentLabels = (
  data: { botId: string; labels: string[] },
  options?: any
) => request<void>('/admin/agent/saveAgentLabels', 'POST', data, options);

// ==================== AI Usage Limits ====================

export interface AiUsageLimitItemDTO {
  limitType: string;
  limitValue: string;
}

export interface AiUsageLimitItem {
  id: number;
  foreignKey: number;
  foreignKeyId: string;
  targetName: string;
  limits: AiUsageLimitItemDTO[];
}

export const AI_USAGE_FOREIGN_KEY = {
  ALL: 1,
  DEPARTMENT: 2,
  INDIVIDUAL: 3,
} as const;

export const AI_USAGE_LIMIT_TYPE = {
  VIDEO_COUNT: 'video_count_limit',
  VIDEO_MINUTES: 'video_minutes_limit',
  CHAT_LIMIT: 'chat_limit',
} as const;

export const feimaChatSuperAdminListLimits = (
  data: PageQuery,
  options?: any
) => request<PageResult<AiUsageLimitItem>>('/admin/aiUsage/listLimits', 'POST', data, options);

export const feimaChatSuperAdminQueryLimit = (
  data: { foreignKey: number; foreignKeyId: string },
  options?: any
) => request<AiUsageLimitItem>('/admin/aiUsage/queryLimit', 'POST', data, options);

export const feimaChatSuperAdminAddLimit = (
  data: { foreignKey: number; foreignKeyId: string; limits: AiUsageLimitItemDTO[] },
  options?: any
) => request<void>('/admin/aiUsage/addLimit', 'POST', data, options);

export const feimaChatSuperAdminUpdateLimit = (
  data: { id: number; limits: AiUsageLimitItemDTO[] },
  options?: any
) => request<void>('/admin/aiUsage/updateLimit', 'POST', data, options);

export const feimaChatSuperAdminDeleteLimit = (
  data: { id: number },
  options?: any
) => request<void>('/admin/aiUsage/deleteLimit', 'POST', data, options);

// ==================== Workspace ====================

export interface AgentAccessControl {
  id: number;
  botId: string;
  foreignKey: number;
  foreignKeyId: string;
  foreignKeyName: string;
  opt: number;
}

export interface InstalledAgentItem {
  botId: string;
  name: string;
  avatar: string;
  description: string;
  categoryId?: number | null;
  scope: number;
  scopeName?: string;
  installEnabled: number;
  accessControls: AgentAccessControl[] | null;
  labels?: string[];
}

export interface FeaturedAgentItem {
  botId: string;
  name: string;
  avatar: string;
  description: string;
  categoryId?: number | null;
  source: string;
  label?: string;
  creatorName?: string;
  createTime?: string;
  usageCount?: number;
  followCount?: number;
}

export interface AgentAccessControlDTO {
  foreignKey: number;
  foreignKeyId: string;
  opt: number;
}

// TODO: 确认 scope 枚举值
export const AGENT_SCOPE = {
  ALL: 1,
  DEPARTMENT: 2,
  SPECIFIC: 3,
} as const;

export const ACCESS_CONTROL_OPT = {
  DENY: 0,
  ALLOW: 1,
} as const;

// TODO: 确认 installEnabled 枚举值
export const INSTALL_ENABLED = {
  DISABLED: 0,
  ENABLED: 1,
} as const;

export const feimaChatSuperAdminListInstalledAgents = (
  data: PageQuery & { keyword?: string; scope?: number },
  options?: any
) => request<PageResult<InstalledAgentItem>>('/admin/workspace/listInstalledAgents', 'POST', data, options);

export const feimaChatSuperAdminListFeaturedAgents = (
  data: PageQuery & { keyword?: string; label?: string },
  options?: any
) => request<PageResult<FeaturedAgentItem>>('/admin/workspace/listFeaturedAgents', 'POST', data, options);

export const feimaChatSuperAdminSaveAgentConfig = (
  data: {
    botId: string;
    foreignKey?: number;
    foreignKeyIds?: string[];
    opt: number;
    categoryId?: number | null;
  },
  options?: any
) => request<void>('/admin/workspace/saveAgentConfig', 'POST', data, options);

export const feimaChatSuperAdminUpdateAgentStatus = (
  data: { botId: string; status: number },
  options?: any
) => request<void>('/admin/agent/updateAgentStatus', 'POST', data, options);

export const feimaChatSuperAdminListAgentAccessControls = (
  data: { botId: string; foreignKey?: number; opt?: number; page?: number; size?: number; isLimit?: boolean },
  options?: any
) => request<PageResult<AgentAccessControl>>('/admin/workspace/listAgentAccessControls', 'POST', data, options);

export const feimaChatSuperAdminUninstallAgent = (
  data: { botId: string, installEnabled: 0 | 1 }, // 0 卸载, 1 安装
  options?: any
) => request<void>('/admin/workspace/uninstallAgent', 'POST', data, options);

// ==================== Personalization ====================

export interface NavTabDTO {
  id: string;
  label: string;
  enabled: boolean;
  order: number;
}

export interface PersonalizationConfigResult {
  themeColor: string;
  productName: string;
  productSlogan: string;
  logoUrl: string;
  websiteNavTabs: NavTabDTO[];
  appNavTabs: NavTabDTO[];
  platform?: string;
}

export const feimaChatSuperAdminGetPersonalizationConfig = (platform: string, options?: any) =>
  request<PersonalizationConfigResult>('/admin/personalization/getConfig', 'POST', { platform }, options);

export const feimaChatSuperAdminSavePersonalizationConfig = (
  data: PersonalizationConfigResult & { platform: string },
  options?: any
) => request<void>('/admin/personalization/saveConfig', 'POST', data, options);

export const feimaChatSuperAdminUploadLogo = (file: File, options?: any) => {
  const formData = new FormData();
  formData.append('file', file);
  return request<string>('/admin/personalization/uploadLogo', 'POST', formData, options);
};

// ==================== System Config ====================

export interface SysConfigGetRequest {
  key: string;
}

export interface SysConfigSaveRequest {
  key: string;
  value: string;
}

export const feimaChatSuperAdminGetSysConfig = (
  data: SysConfigGetRequest,
  options?: any
) => request<string>('/sys/config/get', 'POST', data, options);

export const feimaChatSuperAdminSaveSysConfig = (
  data: SysConfigSaveRequest,
  options?: any
) => request<void>('/admin/sys/config/save', 'POST', data, options);
